import { userEmail } from 'packages/lib/auth';
import { FC, useEffect } from 'react';
import useSWR from 'swr';
import { taskQueue } from 'packages/task-man/service/pickup';
import { pickupTask, pickupManyTasks } from 'packages/task-man/service/edit';
import { updateTaskState } from 'packages/task-man/service/edit/update-task-state';
import retry from 'async-retry';
import { apiGet } from 'packages/lib/api/annotations/rest';
import { taskCountRespZod } from 'packages/lib/api/annotations/contract/task';
import { queueEvent } from 'packages/lib/metrics/helpers';
import { getBaseURLForAPIBackend } from 'packages/lib/utils/fetch';
import { logError } from 'packages/lib/utils/logger';

declare global {
  interface Window {
    taskQueue: typeof taskQueue | undefined;
    pickupTask: typeof pickupTask | undefined;
    pickupManyTasks: typeof pickupManyTasks | undefined;
    updateTaskState: typeof updateTaskState | undefined;
    retry: typeof retry | undefined;
    getTaskManHelpers: typeof getTaskManHelpers | undefined;
    sendDataVolumeMetrics(): void;
  }
}

const reportCount = async (path: string) => {
  const countObj = await apiGet(path, taskCountRespZod.parse);
  const count = countObj['*'];
  const requestUrl = `${getBaseURLForAPIBackend('annotations')}${path}`;
  queueEvent({
    value: count,
    event_name: 'resource_metric',
    sub_event_name: 'data_count',
    timestamp: new Date().toISOString(),
    requestUrl,
  }).catch(logError);
  return count;
};

export function sendDataVolumeMetrics() {
  [
    '/tasks/count',
    '/task-groups/count',
    '/sample-groups/count',
    '/samples/count',
    '/pipelines/count',
    '/workflow/count',
    '/lidar-extractions/count',
    '/image-data/count',
    '/video-extractions/count',
  ].forEach((path) => reportCount(path).catch(logError));
}
window.sendDataVolumeMetrics = sendDataVolumeMetrics;

/**
 * Attaches Task Queue helpers to the browser window so that it's easy
 * to pickup unassigned tasks.
 *
 * We don't have to worry about circularity in test coverage because we
 * ensure great coverage of these utils with unit tests.
 *
 * As a reasonable scoping practice, and for simplicity this is only attached
 * to the window by visiting the task portal landing page.
 */
export const TestAutomationHelperEffects: FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { data: currentUser } = useSWR('user-email', userEmail);
  useEffect(() => {
    if (!currentUser) return;
    const shouldExposeTaskmanMethods = [
      'playwright.test',
      'allen.hart',
      'vern.jensen',
      'kyle.flynn',
    ].find((username) => currentUser.includes(username));
    if (shouldExposeTaskmanMethods) {
      window.taskQueue = taskQueue;
      window.pickupTask = pickupTask;
      window.pickupManyTasks = pickupManyTasks;
      window.updateTaskState = updateTaskState;
      window.retry = retry;
      window.getTaskManHelpers = getTaskManHelpers;
    }
  }, [currentUser]);
  return null;
};

export function getTaskManHelpers() {
  if (!window.taskQueue) throw new Error('window.taskQueue is undefined.');
  if (!window.pickupTask) throw new Error('window.pickupTask is undefined.');
  if (!window.pickupManyTasks)
    throw new Error('window.pickupManyTasks is undefined.');
  if (!window.updateTaskState)
    throw new Error('window.updateTaskState is undefined.');
  if (!window.retry) throw new Error('window.retry is undefined.');
  const { taskQueue, pickupTask, pickupManyTasks, updateTaskState, retry } =
    window;
  return {
    taskQueue,
    pickupTask,
    pickupManyTasks,
    updateTaskState,
    retry,
  };
}
